import React, { useState } from 'react';
import { Calendar, AlertTriangle } from 'lucide-react';
import { Modal, FormField } from './Table';
import api from '../utils/api';
import toast from 'react-hot-toast';

const REASON_TYPES = ['Govt Extension', 'Client Request', 'Internal Delay', 'Holiday', 'System Error', 'Other'];

export default function DueDateEditModal({ record, tableName, moduleName, onClose, onSaved }) {
  const origDate = record.due_date || '';
  const [form, setForm] = useState({
    revised_due_date: record.revised_due_date || origDate,
    reason_type: record.due_date_reason || 'Govt Extension',
    reason_notes: '',
    govt_extended: record.govt_extended === 1,
  });
  const [saving, setSaving] = useState(false);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.revised_due_date) { toast.error('Please enter a revised due date'); return; }
    setSaving(true);
    try {
      await api.post('/accounts/revise-due-date', {
        module: moduleName,
        entity_id: record.id,
        table_name: tableName,
        original_due_date: origDate,
        revised_due_date: form.revised_due_date,
        reason_type: form.reason_type,
        reason_notes: form.reason_notes,
        govt_extended: form.govt_extended,
      });
      toast.success('Due date updated');
      onSaved();
      onClose();
    } catch (err) {
      console.error('revise-due-date error:', err?.response?.data || err?.message);
      toast.error(err?.response?.data?.error || 'Failed to update due date');
    }
    finally { setSaving(false); }
  };

  return (
    <Modal title="Edit Due Date" onClose={onClose} size="sm">
      <div className="space-y-4">
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm">
          <span className="text-blue-600 font-medium">Original due date: </span>
          <span className="text-blue-800 font-semibold">{origDate || '—'}</span>
          {record.client_name && <span className="text-blue-500 ml-2">— {record.client_name}</span>}
        </div>

        <FormField label="Revised Due Date" required>
          <input type="date" value={form.revised_due_date} onChange={e => f('revised_due_date', e.target.value)} className="input-field" />
        </FormField>

        <FormField label="Reason">
          <select value={form.reason_type} onChange={e => f('reason_type', e.target.value)} className="input-field">
            {REASON_TYPES.map(r => <option key={r}>{r}</option>)}
          </select>
        </FormField>

        <FormField label="Notes / Reference">
          <input value={form.reason_notes} onChange={e => f('reason_notes', e.target.value)} className="input-field" placeholder="e.g. CBDT Circular No. 12/2025..." />
        </FormField>

        <label className="flex items-center gap-3 cursor-pointer select-none">
          <input type="checkbox" checked={form.govt_extended} onChange={e => f('govt_extended', e.target.checked)}
            className="w-4 h-4 accent-[#f0a500]" />
          <div>
            <div className="text-sm font-medium text-gray-700">Government Extension</div>
            <div className="text-xs text-gray-400">Shows "Govt Ext" badge on War Room and marks as govt-extended in reports</div>
          </div>
        </label>

        {form.govt_extended && (
          <div className="flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-lg p-2 text-xs text-orange-700">
            <AlertTriangle size={13} /> This will display an orange "Govt Ext" badge on the War Room dashboard
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <button onClick={save} disabled={saving} className="btn-primary flex-1 disabled:opacity-50">
            {saving ? 'Saving...' : 'Save Due Date'}
          </button>
          <button onClick={onClose} className="btn-secondary flex-1">Cancel</button>
        </div>
      </div>
    </Modal>
  );
}
